import React, { useState, useEffect } from 'react'
import { Button, Header, Image, Modal, Popup } from 'semantic-ui-react'
import close_modal from '../../assets/svg/close_modal.svg'
import { IWizardProject } from '../../views/project/types/wizardData'

interface IStepString {
  key: number
  name: string
}

interface Props {
  open: boolean;
  close: () => void
  setWizardStep?: (step: string) => void
  setProjectWizardData?: (data: IWizardProject) => void
  data?: IWizardProject
  headerContent?: string
  headerSubContent?: string
  content: JSX.Element
  footer?: JSX.Element
  minHeight?: string
  maxHeight?: string
  headerComponent?: JSX.Element
  width?: string
  minWidth?: string
  maxWidth?: string
  modalClassName?: string
  /** Let the footer size to its content instead of the fixed 70px band. */
  footerFitContent?: boolean
  borderHeader?: string
  steps?: number
  activeStep?: number
  finishedSteps?: number[]
  headerColor?: string
  hidePopup?: boolean
  headerContentFontSize?: string
  headerFontWeight?: string | number
  alignItemsStart?: boolean
  marginTop?: string
  marginHeaderTop?: string
  marginHeaderLeft?: string
  contentPadding?: string
  actionPadding?: string
  borderTopFooter?: string
  removeSteps?: boolean
  stepStringList?: IStepString[]
  contentOverflowX?: string
  contentOverflowY?: string
}

const WizardComponent = (props: Props): JSX.Element => {
  const [openPopup, setOpenPopup] = useState(false)

  // Reset popup state when modal closes to prevent it from persisting
  useEffect(() => {
    if (!props.open) {
      setOpenPopup(false)
    }
  }, [props.open])

  const handleStepOnClick = (step: number, e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    if ((props.finishedSteps && !props.finishedSteps.includes(step)) || step === 1) {
      e.preventDefault()
    } else if (props.setWizardStep && props.finishedSteps
      && props.setProjectWizardData && props.data && props.stepStringList) {
      props.setWizardStep(props.stepStringList.filter(s => s.key === step)[0].name)
      props.setProjectWizardData(props.data)
    }
  }

  const renderProgress = () => {
    if (props.steps) {
      if (props.removeSteps) {
        return (
          <div data-testid="wizard-progress-indicator" style={{ height: '38px' }} />
        )
      }
      const _steps: number[] = []
      for (let i = 0; i < props.steps; i++) {
        _steps.push(i + 1)
      }
      const lastStepWidth = (305 / _steps.length) / 2
      const stepWith = (lastStepWidth / _steps.length) + (305 / _steps.length)
      const yText = 12.5 + (1.47 / 2)
      return (
        <div data-testid="wizard-progress-indicator">
          <ul style={{ width: '305px', height: '25px', textAlign: 'end', padding: 0, marginTop: '-1rem' }}>
            {_steps.map(s => {
              const _fill = s === props.activeStep ? 'white' : props.finishedSteps?.includes(s) ? '#08D6A1' : '#F3F3F3'
              const _stroke = s === props.activeStep ? '#08D6A1' : props.finishedSteps?.includes(s) ? '#08D6A1' : '#BEC8C3'
              const _text = s === props.activeStep ? '#08D6A1' : props.finishedSteps?.includes(s) ? 'white' : '#BEC8C3'
              const _line = props.finishedSteps?.includes(s) ? '#08D6A1' : '#BEC8C3'

              // Determine step status class for test IDs
              const stepStatus = s === props.activeStep ? 'active' : props.finishedSteps?.includes(s) ? 'completed' : 'inactive'

              return (
                <svg
                  data-testid={`progress-step-${s}`}
                  className={stepStatus}
                  style={{ cursor: (props.finishedSteps && s > props.finishedSteps.length) || s === 1 ? 'no-drop' : 'pointer' }}
                  onClick={(e) => handleStepOnClick(s, e)}
                  height='25'
                  width={s === _steps[_steps.length - 1] ? lastStepWidth : stepWith}
                  key={s}
                >
                  <line x1='22.2%' y1='50%' x2={s === _steps[_steps.length - 1] ? '0' : '100%'} y2='50%' stroke={_line} strokeWidth='1.47' />
                  <circle cx='12.5' cy='12.5' r='12' stroke={_stroke} strokeWidth='1.47' fill={_fill} />
                  <text x='12.5' y={yText} dominantBaseline='middle' textAnchor='middle' fill={_text} fontSize='14'>{s}</text>
                </svg>
              )
            })}
          </ul>
        </div>
      )
    }
    return null
  }

  const renderHeader = () => {
    if (props.headerComponent) {
      return (
        <Modal.Header style={{ borderBottom: props.borderHeader, paddingBottom: '.1rem', color: props.headerColor, fontSize: '28px', fontWeight: 'bold' }} content={props.headerComponent} />
      )
    } else {
      return (
        <Header>
          <Header.Content style={{ display: 'flex', flexDirection: 'column', alignItems: props.alignItemsStart ? 'start' : 'center' }} data-testid="wizard-header">
            {renderProgress()}
            <p style={{ textAlign: 'center', fontSize: props.headerContentFontSize ? props.headerContentFontSize : '28px', fontWeight: props.headerFontWeight, color: props.headerColor, marginLeft: props.marginHeaderLeft, marginTop: props.marginHeaderTop, paddingBottom: '7px' }}>{props.headerContent}</p>
          </Header.Content>
          {props.headerSubContent ? <Header.Subheader style={{ textAlign: 'center' }}>{props.headerSubContent}</Header.Subheader> : null}
        </Header>
      )
    }
  }

  const renderConfirmation = () => {
    return (
      <div data-testid="cancel-confirmation-dialog">
        <p style={{ fontSize: '14px', fontWeight: 'bold', margin: '.3rem', letterSpacing: '.25px', color: '#232C58' }}>Are you sure?</p>
        <Button content='Cancel' onClick={() => setOpenPopup(false)} color='blue' style={{ width: '5rem', height: '2rem', borderRadius: '4.8px', padding: '0rem' }} />
        <Button content='Yes' onClick={() => props.close()} color='red' style={{ width: '3.5rem', height: '2rem', borderRadius: '4.8px', padding: '0rem' }} />
      </div>
    )
  }

  const renderPopop = () => {
    if (!props.hidePopup) {
      const cancel = openPopup ? false : true
      return (
        <Popup
          basic
          open={openPopup}
          trigger={<Image src={close_modal} onClick={() => setOpenPopup(cancel)} style={{ margin: '3% 0 0 95%', cursor: 'pointer' }} />}
          content={renderConfirmation()}
          on='click'
          position='bottom right'
          style={{ borderRadius: '8px', marginTop: '-.2rem' }}
        />
      )
    }
    return (
      <Image src={close_modal} onClick={() => props.close()} style={{ margin: '3% 0 0 95%', cursor: 'pointer' }} />
    )
  }

  return (
    <>
      <style>
        {`
          @media (max-width: 1169px) {
            .ui.modal.wizard-responsive-width {
              width: 90% !important;
            }
          }
        `}
      </style>
      <Modal
        closeIcon={renderPopop()}
        open={props.open}
        style={{ width: props.width ? props.width : '', minWidth: props.minWidth ? props.minWidth : '', maxWidth: props.maxWidth ? props.maxWidth : '' }}
        className={[props.width === '70%' ? 'wizard-responsive-width' : '', props.modalClassName || ''].filter(Boolean).join(' ')}
        data-testid="wizard-component"
      >
      {renderHeader()}
      <Modal.Content
        data-testid="wizard-content"
        style={{
          padding: props.contentPadding ?? '0 3rem 2rem 3rem',
          maxHeight: props.maxHeight ? props.maxHeight : '',
          minHeight: props.minHeight || '',
          marginTop: props.marginTop,
          overflowX: props.contentOverflowX || 'visible',
          overflowY: props.contentOverflowY || 'visible'
      }}>
        {props.content}
      </Modal.Content>
      <Modal.Actions
        data-testid="wizard-footer"
        style={{
          borderTop: props.borderTopFooter ? props.borderTopFooter : '',
          padding: props.actionPadding ?? props.contentPadding ?? '20px 48px',
          backgroundColor: '#F9FBFC',
          /*
           * 70px unless the caller opts out. A Semantic button (~36px) plus a 20px inset
           * either side overflows it by 6px, so the budget dialogs let the band size to its
           * content instead — BudgetModalTemplate passes footerFitContent (issue #126).
           * Everything else keeps the fixed band it has always had.
           */
          height: props.footerFitContent ? undefined : '70px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end'
      }}>
        {props.footer}
      </Modal.Actions>
    </Modal>
    </>
  )
}

export default WizardComponent

